import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { TileRow, TileBox, DocumentFace } from './AttachmentTiles';
import { MAX_FILES_PER_SEND, isImageAttachment } from '../utils/attachments';

// Files chosen but not sent yet, above the message box. Whatever is typed is sent as their
// caption. Small square tiles that scroll sideways, each with a remove button.

const Tray = styled.div`
  margin-bottom: 8px;
`;

const Tile = styled.div`
  position: relative;
  flex: 0 0 auto;
`;

// A preview, not a button
const Preview = styled(TileBox)`
  cursor: default;
  &:hover:not(:disabled) {
    border-color: #d9e0e8;
  }
`;

const Remove = styled.button`
  position: absolute;
  top: 3px;
  right: 3px;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 1px solid #cbd5e1;
  border-radius: 50%;
  background: #ffffff;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  svg {
    width: 10px;
    height: 10px;
  }

  &:hover {
    color: #b91c1c;
    border-color: #b91c1c;
  }
`;

const AddMore = styled.button`
  flex: 0 0 auto;
  width: 64px;
  height: 64px;
  border: 1px dashed #94a3b8;
  border-radius: 12px;
  background: #ffffff;
  color: #275386;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #275386;
  }

  &:disabled {
    color: #cbd5e1;
    border-color: #e2e8f0;
    cursor: not-allowed;
  }
`;

const Note = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: ${props => (props.$error ? '#b91c1c' : '#64748b')};
`;

const AttachmentTray = ({ files, onRemove, onAddMore, error, disabled }) => {
  // Local previews for images; released when the tray changes or closes
  const previews = useMemo(
    () => files.map(file => (isImageAttachment({ file_name: file.name }) ? URL.createObjectURL(file) : null)),
    [files]
  );
  useEffect(() => () => previews.forEach(url => url && URL.revokeObjectURL(url)), [previews]);

  if (!files.length && !error) return null;
  return (
    <Tray>
      {files.length > 0 && (
        <TileRow>
          {files.map((file, index) => (
            <Tile key={`${file.name}-${file.size}-${file.lastModified}-${index}`}>
              <Preview as="div" $size={64} title={file.name}>
                {previews[index] ? <img src={previews[index]} alt={file.name} /> : <DocumentFace name={file.name} small />}
              </Preview>
              <Remove type="button" onClick={() => onRemove(index)} aria-label={`Remove ${file.name}`} title="Remove">
                <svg viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </Remove>
            </Tile>
          ))}
          <AddMore
            type="button"
            onClick={onAddMore}
            disabled={disabled || files.length >= MAX_FILES_PER_SEND}
            aria-label="Add more files"
            title={files.length >= MAX_FILES_PER_SEND ? `Up to ${MAX_FILES_PER_SEND} files at a time` : 'Add more files'}
          >
            +
          </AddMore>
        </TileRow>
      )}
      {error
        ? <Note $error>{error}</Note>
        : <Note>{files.length === 1 ? '1 file' : `${files.length} files`} · add a caption below, then send</Note>}
    </Tray>
  );
};

export default AttachmentTray;
